// src/components/EvaluationConfigurationStepFour.js
import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Work as WorkIcon,
  Star as StarIcon,
  Group as GroupIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  Quiz as QuizIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import api from '../config/api';
import useStore from '../config/store';
import { useTranslation } from 'react-i18next';
import EvaluationAlert from './EvaluationAlert';

const EvaluationConfigurationStepFour = ({
  selectedPosition,
  competenciesConfig,
  candidatesConfig,
  evaluationData,
  onResetToStep1, // Nueva prop para resetear al paso 1
}) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [createdEvaluationId, setCreatedEvaluationId] = useState(null);
  const [sendEmails, setSendEmails] = useState(false);
  const [canSendToAll, setCanSendToAll] = useState(true);

  const { authToken, showAlert, organization, project, user, language } = useStore.getState();
  const { t } = useTranslation();

  // Función para crear nueva evaluación (resetear al paso 1)
  const handleCreateNewEvaluation = () => {
    setSuccess(false);
    setCreatedEvaluationId(null);
    setError(null);
    setLoading(false);
    setSendEmails(true); // Resetear el switch

    // Llamar función del componente padre para resetear todo y volver al paso 1
    if (onResetToStep1) {
      onResetToStep1();
    }
  };

  const handleUsageCheckComplete = (data) => {
    const remaining = data.remaining || 0;
    const candidatesCount = candidatesConfig.candidates.length;
    const canSend = data.unlimited || remaining >= candidatesCount;
    setCanSendToAll(canSend);
  };

  // API Function para crear evaluación
  const createEvaluationAPI = async (evaluationData) => {
    const response = await api.post(
      "/evaluations",
      { ...evaluationData, user: user?.email },
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  };

  // API Function para crear evaluationResponse
  const createEvaluationResponse = async (evaluationResponseData) => {
    const response = await api.post(
      "/evaluation-responses",
      evaluationResponseData,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );
    return response.data;
  };

  // API Function para actualizar estado del applicant (enviar invitación)
  const sendInvitationAPI = async (evaluationId, applicantId) => {
    const response = await api.put(
      `/evaluations/${evaluationId}/applicants/${applicantId}/status`,
      { status: 'sent' },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  // Casos de estudio del assessment (snapshot, PLAN_CASOS_ESTUDIO.md §4.3).
  // Vienen del paso 2 agrupados por caso y en orden de primera aparición.
  const assessmentCaseStudies = (competenciesConfig.caseStudies || []).map((cs, index) => ({
    caseStudyId: cs.caseStudyId,
    title: cs.title || '',
    content: cs.content || '',
    readingTime: Number(cs.readingTime) || 5,
    order: index + 1,
  }));

  // timeLimit = Σ lectura por caso (una vez) + Σ respuesta por competencia.
  // Number(): los TextField del paso 2 pueden dejar valores como string.
  const computeTotalTime = () =>
    assessmentCaseStudies.reduce((total, cs) => total + (Number(cs.readingTime) || 5), 0) +
    competenciesConfig.capabilities.reduce((total, comp) => total + (Number(comp.questionTime) || 10), 0);

  const readingTimeForCompetency = (comp) => {
    const cs = assessmentCaseStudies.find((c) => c.caseStudyId === comp.selectedText);
    return cs ? cs.readingTime : (Number(comp.selectedCaseReadingTime) || 5);
  };

  // Función para crear la evaluación
  const handleCreateEvaluation = async () => {
    try {
      setLoading(true);
      setError(null);

      const evaluationPayload = {
        projectId: project,
        organizationId: organization,
        positionId: selectedPosition.id || selectedPosition.uid,
        positionName: selectedPosition.name,
        title: evaluationData.title,
        description: evaluationData.description || '',
        // Modo simple / completo (PLAN_UNIFICACION_ASSESSMENT.md)
        mode: evaluationData.mode || 'complete',
        // timeLimit se recalcula aquí (y también en el backend) desde los
        // tiempos ya normalizados a número, en vez de confiar en
        // evaluationData.timeLimit: si algún tiempo venía como string, la suma
        // upstream concatenaba ("35"+"15"="3515") y el timeLimit corrupto
        // rompía el inicio de la evaluación pública.
        timeLimit: computeTotalTime() || 15,
        // Casos de estudio del assessment: el candidato lee cada uno UNA vez
        caseStudies: assessmentCaseStudies,
        capabilities: competenciesConfig.capabilities.map((comp, index) => ({
          capabilityId: comp.capability,
          capabilityName: comp.capabilityName,
          weight: parseFloat(comp.percentage) / 100,
          order: index + 1,
          // ✅ CAMBIO PRINCIPAL: Mapear múltiples preguntas por competencia
          questions: comp.questions?.map((questionText, qIndex) => ({
            id: `q${index + 1}_${qIndex + 1}`, // Ej: q1_1, q1_2, q1_3
            text: questionText,
            type: "open_text",
            required: true,
            maxLength: 500,
            order: qIndex + 1,
            competencyOrder: index + 1
          })) || [],
          // Caso de estudio al que apunta la competencia (snapshot en caseStudies[])
          caseStudyId: comp.selectedText || '',
          evaluationCriteria: comp.evaluationCriteria || '',
          // Number(): los TextField del paso 2 pueden dejar estos valores
          // como string; se normalizan antes de persistir.
          // El tiempo de lectura ya no va por competencia (es del caso).
          questionTime: Number(comp.questionTime) || 10
        })),
        applicants: candidatesConfig.candidates.map(candidate => ({
          applicantId: candidate.id || candidate.uid,
          name: candidate.name,
          email: candidate.email,
          status: 'pending',
          invitedAt: null,        // No se envía invitación al crear
          firstOpenedAt: null,
          startedAt: null,
          completedAt: null,
          isExpired: false
        })),
        isActive: true
      };

      const response = await createEvaluationAPI(evaluationPayload);

      if (response.response && response.response.status_response === 'OK') {
        const evaluationId = response.response.data.id;
        setCreatedEvaluationId(evaluationId);

        // Si el switch está activado, generar evaluationResponses y enviar emails
        if (sendEmails) {
          let emailSuccessCount = 0;
          let emailErrorCount = 0;

          for (const candidate of candidatesConfig.candidates) {
            try {
              // 1. Crear evaluationResponse
              const evaluationResponseData = {
                evaluationId: evaluationId,
                applicantId: candidate.id || candidate.uid,
                creatorEmail: user?.email,
                projectId: project,
                organizationId: organization
              };

              const responseCreated = await createEvaluationResponse(evaluationResponseData);

              if (responseCreated.response && responseCreated.response.status_response === 'OK') {
                // 2. Enviar email
                const emailData = {
                  candidateEmail: candidate.email || '',
                  creatorEmail: user?.email || 'support@amandahr.ai',
                  positionName: selectedPosition.name || t('evaluations.unspecified'),
                  candidateName: candidate.name || t('evaluations.candidate'),
                  language: language || 'es',
                  projectId: project,
                  organizationId: organization
                };

                try {
                  const emailResponse = await api.post(
                    `/evaluation-responses/${responseCreated.response.data.id}/send-email`,
                    emailData,
                    {
                      headers: { Authorization: `Bearer ${authToken}` }
                    }
                  );

                  if (emailResponse.data.errorCode === 0) {
                    console.log(`Email enviado exitosamente a ${candidate.name}`);
                  } else {
                    console.error(`Error al enviar email a ${candidate.name}:`, emailResponse.data.errorMessage);
                    emailErrorCount++;
                  }
                } catch (emailError) {
                  console.error(`Error al enviar email de invitación a ${candidate.name}:`, emailError);
                  emailErrorCount++;
                }

                // 3. Actualizar estado del applicant en la evaluación
                try {
                  const updateResponse = await sendInvitationAPI(evaluationId, candidate.id || candidate.uid);
                  if (updateResponse.response && updateResponse.response.status_response === 'OK') {
                    emailSuccessCount++;
                  }
                } catch (updateError) {
                  console.error(`Error al actualizar estado del candidato ${candidate.name}:`, updateError);
                  emailErrorCount++;
                }
              } else {
                console.error(`Error al crear evaluationResponse para ${candidate.name}`);
                emailErrorCount++;
              }
            } catch (candidateError) {
              console.error(`Error procesando candidato ${candidate.name}:`, candidateError);
              emailErrorCount++;
            }
          }

          // Mostrar resultado de envío de emails
          if (emailSuccessCount > 0) {
            showAlert(t('evaluationResponses.evaluationsGeneratedSuccessfully', { count: emailSuccessCount }), 'success');
          }
          if (emailErrorCount > 0) {
            showAlert(t('evaluationResponses.evaluationsFailed', { count: emailErrorCount }), 'warning');
          }
        }

        setSuccess(true);
        showAlert(t('evaluations.evaluationCreateSuccess'), 'success');
      } else {
        throw new Error(response.response?.message || 'Error al crear la evaluación');
      }

    } catch (error) {
      console.error('Error creating evaluation:', error);
      setError(t('evaluations.evaluationCreateError'));
      showAlert(t('evaluations.evaluationCreateError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('evaluations.evaluationCreateSuccess')}
          </Typography>
          <Typography variant="body2">
            {sendEmails
              ? t('evaluations.evaluationCreateSuccessWithEmails')
              : t('evaluations.evaluationCreateSuccessInfo')
            }
          </Typography>
          {createdEvaluationId && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('evaluations.evaluationId')}: <strong>{createdEvaluationId}</strong>
            </Typography>
          )}
        </Alert>

        <Paper sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <CheckCircleIcon sx={{ mr: 1, color: 'success.main' }} />
            {t('nextSteps')}
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            {sendEmails
              ? t('evaluations.evaluationReadyWithEmails')
              : t('evaluations.evaluationReady')
            }
          </Typography>

          <Box sx={{ ml: 2, mb: 3 }}>
            {sendEmails ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • {t('evaluations.emailsSentToCandidates')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • {t('evaluations.candidatesCanStartEvaluation')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • {t('evaluations.monitorProgressInEvaluationView')}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • {t('evaluations.evaluationReadyPointOne')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • {t('evaluations.evaluationReadyPointTwo')}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  • {t('evaluations.evaluationReadyPointThree')}
                </Typography>
              </>
            )}
          </Box>

          {/* Botón para crear nueva evaluación */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={<AssignmentIcon />}
              onClick={handleCreateNewEvaluation}
              sx={{ minWidth: 250 }}
            >
              {t('evaluations.createNewEvaluation')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
        {t('evaluations.evaluationCreateConfirm')}
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t('evaluations.evaluationCreateReview')}
      </Typography>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* NUEVO: Alert de assessments disponibles */}
      <EvaluationAlert
        pendingCount={candidatesConfig.candidates.length}
        showDetailedInfo={candidatesConfig.candidates.length > 0}
        onUsageCheckComplete={handleUsageCheckComplete}
      />

      {/* Resumen de la configuración */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <CheckCircleIcon sx={{ mr: 1, color: 'primary.main' }} />
          {t('evaluations.evaluationSummary')}
        </Typography>

        {/* Información Básica */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon sx={{ mr: 1, fontSize: 20 }} />
            {t('basicInfo')}
          </Typography>
          <Box sx={{ ml: 3, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t('title')}:</strong> {evaluationData.title}
            </Typography>
            {evaluationData.description && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t('description')}:</strong> {evaluationData.description}
              </Typography>
            )}
            <Typography variant="body2">
              <strong>{t('timeLimit')}:</strong> {evaluationData.timeLimit} {t('minutes')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Cargo */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <WorkIcon sx={{ mr: 1, fontSize: 20 }} />
            {t('position')}
          </Typography>
          <Box sx={{ ml: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>{t('name')}:</strong> {selectedPosition?.name}
            </Typography>
            <Typography variant="body2">
              <strong>{t('industry')}:</strong> {selectedPosition?.industria}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Competencias */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <StarIcon sx={{ mr: 1, fontSize: 20 }} />
            {t('drawer.capabilities')} ({competenciesConfig.capabilities.length})
          </Typography>
          <Box sx={{ ml: 3 }}>
            {competenciesConfig.capabilities.map((comp, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 2 }}>
                    {comp.capabilityName}
                  </Typography>
                  <Chip
                    label={`${comp.percentage}%`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={t('evaluations.questionsCount', { count: comp.questions?.length || 0 })}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                  <Chip
                    label={`${t('evaluations.questions')}: ${Number(comp.questionTime) || 10} ${t('evaluations.minutes')}`}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                </Box>

                {/* Caso de estudio al que apunta (se lee una vez por caso) */}
                {comp.selectedTextTitle && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    <strong>{t('evaluations.caseStudy')}:</strong> {comp.selectedTextTitle}
                    {` · ${t('evaluations.reading')}: ${readingTimeForCompetency(comp)} ${t('evaluations.minutes')}`}
                    {assessmentCaseStudies.length < competenciesConfig.capabilities.length && (
                      <> · {t('evaluations.sharedCaseHint')}</>
                    )}
                  </Typography>
                )}

                {/* ✅ CAMBIO: Mostrar múltiples preguntas en accordion */}
                {comp.questions && comp.questions.length > 0 && (
                  <Accordion sx={{ mt: 1 }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 'auto', '& .MuiAccordionSummary-content': { margin: '8px 0' } }}
                    >
                      <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <QuizIcon sx={{ fontSize: 16, mr: 0.5 }} />
                        {t('evaluations.viewQuestions')} ({comp.questions.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Box sx={{ pl: 1 }}>
                        {comp.questions.map((question, qIndex) => (
                          <Typography
                            key={qIndex}
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}
                          >
                            <strong>{qIndex + 1}.</strong> {question}
                          </Typography>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Advertencia si no hay preguntas */}
                {(!comp.questions || comp.questions.length === 0) && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      {t('evaluations.noQuestionsConfigured')}
                    </Typography>
                  </Alert>
                )}
              </Box>
            ))}

            {/* Resumen total */}
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`${t('total')}: ${competenciesConfig.totalPercentage}%`}
                color="success"
                variant="filled"
              />
              <Chip
                label={`${t('evaluations.totalQuestions')}: ${competenciesConfig.capabilities.reduce((total, comp) => total + (comp.questions?.length || 0), 0)}`}
                color="info"
                variant="filled"
              />
              <Chip
                label={`${t('evaluations.totalTime')}: ${computeTotalTime()} ${t('evaluations.minutes')}`}
                color="warning"
                variant="filled"
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Candidatos */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <GroupIcon sx={{ mr: 1, fontSize: 20 }} />
            {t('applicants.title')} ({candidatesConfig.candidates.length})
          </Typography>
          <Box sx={{ ml: 3 }}>
            {candidatesConfig.candidates.map((candidate, index) => (
              <Box key={index} sx={{ mb: 1, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {candidate.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {candidate.email} {candidate.phone && `• ${candidate.phone}`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      {/* Switch para envío de emails */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9' }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
          {t('evaluations.emailNotifications')}
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={sendEmails}
              onChange={(e) => setSendEmails(e.target.checked)}
              color="primary"
              disabled={loading || !canSendToAll}
            />
          }
          label={
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {t('evaluations.sendEmailsToCandidate')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {sendEmails
                  ? t('evaluations.emailsWillBeSentAutomatically')
                  : t('evaluations.emailsWillNotBeSent')
                }
              </Typography>
              {!canSendToAll && (
                <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                  {t('evaluations.insufficientAssessments')}
                </Typography>
              )}
            </Box>
          }
          sx={{
            alignItems: 'flex-start',
            ml: 0,
            '& .MuiFormControlLabel-label': { mt: 0.5 }
          }}
        />

        {sendEmails && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t('evaluations.emailWillInclude')}
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Botón de crear evaluación */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          onClick={handleCreateEvaluation}
          disabled={loading}
          sx={{ minWidth: 200 }}
        >
          {loading
            ? (sendEmails ? t('evaluations.creatingAndSendingEmails') : t('evaluations.creatingEvaluation'))
            : t('evaluations.createEvaluation')
          }
        </Button>
      </Box>
    </Box>
  );
};

export default EvaluationConfigurationStepFour;